/**
 * Azure AD API Client
 *
 * Provides methods for interacting with Azure AD / Entra ID directory endpoints
 */

import { apiFetch } from './client';

/**
 * Azure AD user information from Microsoft Graph
 */
export interface AzureADUser {
  displayName: string;
  mail: string | null;
  jobTitle: string | null;
  department: string | null;
  phone: string | null; // Normalized from mobilePhone or businessPhones[0]
  officeLocation: string | null;
}

/**
 * Paginated response for Azure AD user list
 */
export interface AzureADUserListResponse {
  users: AzureADUser[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Parameters for fetching AIU users
 */
export interface GetAIUUsersParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Azure AD API client
 */
export const azureAdApi = {
  /**
   * Get AIU users from Azure AD
   *
   * Retrieves a paginated list of AIU staff from the Azure AD directory.
   * Results are cached on the server for 10 minutes.
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Promise resolving to paginated user list
   *
   * @example
   * // Get first page of all users
   * const users = await azureAdApi.getAIUUsers();
   *
   * @example
   * // Search for users
   * const results = await azureAdApi.getAIUUsers({ search: 'john' });
   *
   * @example
   * // Get specific page
   * const page2 = await azureAdApi.getAIUUsers({ page: 2, pageSize: 50 });
   */
  async getAIUUsers(params?: GetAIUUsersParams): Promise<AzureADUserListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = `/azure-ad/users${queryString ? `?${queryString}` : ''}`;

    return await apiFetch<AzureADUserListResponse>(endpoint);
  },

  /**
   * Clear the Azure AD user cache on the server
   *
   * Forces a fresh fetch from Azure AD on the next request.
   * Useful when immediate updates are needed.
   *
   * @returns Promise resolving to success message
   *
   * @example
   * await azureAdApi.clearCache();
   */
  async clearCache(): Promise<{ message: string }> {
    return await apiFetch<{ message: string }>('/azure-ad/cache/clear', {
      method: 'POST'
    });
  }
};
